import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import TournamentAdminPanel from './TournamentAdminPanel';
import AdminSupportSection from './AdminSupportSection';

const VIEWS = {
  dashboard: 'dashboard',
  agents: 'agents',
  agentDetails: 'agentDetails',
  customers: 'customers',
  customerDetails: 'customerDetails',
  tournaments: 'tournaments',
  tournamentDetails: 'tournamentDetails',
  transactions: 'transactions',
  activities: 'activities',
  games: 'games',
  onlinePlayers: 'onlinePlayers',
  emailActivities: 'emailActivities',
  support: 'support',
};

const EMPTY_DASHBOARD_STATS = {
  cards: { agents: 0, customers: 0, coins: 0 },
  realtime: { ongoingMatches: 0, gamesToday: 0, gamesThisWeek: 0 },
  actorBalance: 0,
  companyWallet: null,
};

export default function AdminPanel({ user, onLogout, routeInfo, onNavigate }) {
  const LIST_PAGE_SIZE = 10;
  const isSuperadmin = user?.role === 'superadmin' || user?.isAdmin;

  const isViewAllowed = useCallback((nextView) => {
    if (isSuperadmin) return true;
    return ![VIEWS.agents, VIEWS.agentDetails, VIEWS.emailActivities].includes(nextView);
  }, [isSuperadmin]);
  const [view, setView] = useState(VIEWS.dashboard);
  const [search, setSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [activityScope] = useState('customer');
  const [activityPage, setActivityPage] = useState(1);
  const [agentListPage, setAgentListPage] = useState(1);
  const [agentProfileCustomerPage, setAgentProfileCustomerPage] = useState(1);
  const [customerListPage, setCustomerListPage] = useState(1);
  const [gamesPage, setGamesPage] = useState(1);
  const [gamesSearch, setGamesSearch] = useState('');
  const [gamesRange, setGamesRange] = useState('today');
  const [onlinePlayersPage, setOnlinePlayersPage] = useState(1);
  const [onlinePlayersSearch, setOnlinePlayersSearch] = useState('');
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [onlinePlayersMeta, setOnlinePlayersMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [emailActivitiesPage, setEmailActivitiesPage] = useState(1);
  const [emailActivitiesSearch, setEmailActivitiesSearch] = useState('');
  const [emailActivities, setEmailActivities] = useState([]);
  const [emailActivitiesMeta, setEmailActivitiesMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [activities, setActivities] = useState([]);
  const [activitiesMeta, setActivitiesMeta] = useState({ page: 1, totalPages: 1, total: 0, scope: 'customer' });
  const [myTransactions, setMyTransactions] = useState([]);
  const [myTransactionsMeta, setMyTransactionsMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsFilter, setTransactionsFilter] = useState('all');
  const [games, setGames] = useState([]);
  const [gamesMeta, setGamesMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedTournamentAdminId, setSelectedTournamentAdminId] = useState('');
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentStats, setAgentStats] = useState({ totalAgents: 0, activeAgents: 0, topAgent: null });
  const [dashboardStats, setDashboardStats] = useState(EMPTY_DASHBOARD_STATS);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedAgentProfileId, setSelectedAgentProfileId] = useState('');
  const [agentProfile, setAgentProfile] = useState(null);
  const [agentProfileCustomers, setAgentProfileCustomers] = useState([]);
  const [loadingAgentProfile, setLoadingAgentProfile] = useState(false);
  const [selectedCustomerProfileId, setSelectedCustomerProfileId] = useState('');
  const [customerProfile, setCustomerProfile] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [customerOnlineSessions, setCustomerOnlineSessions] = useState([]);
  const [customerOnlineSessionsMeta, setCustomerOnlineSessionsMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
  const [loadingCustomerProfile, setLoadingCustomerProfile] = useState(false);
  const [customerWalletAmount, setCustomerWalletAmount] = useState('500');
  const [customerWalletMode, setCustomerWalletMode] = useState('credit');
  const [customerAssignAgentId, setCustomerAssignAgentId] = useState('');
  const [agentWalletAmount, setAgentWalletAmount] = useState('500');
  const [agentWalletMode, setAgentWalletMode] = useState('credit');
  const [note, setNote] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    agentId: '',
  });
  const [agentForm, setAgentForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    commissionRate: '0',
  });
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingOnlinePlayers, setLoadingOnlinePlayers] = useState(false);
  const [loadingEmailActivities, setLoadingEmailActivities] = useState(false);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [submittingRecharge, setSubmittingRecharge] = useState(false);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [submittingCommission, setSubmittingCommission] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [commissionRateInput, setCommissionRateInput] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [staffCoins, setStaffCoins] = useState(Number(user?.coins || 0));
  const shouldRefreshDirectory = [VIEWS.dashboard, VIEWS.agents, VIEWS.customers].includes(view);

  useEffect(() => {
    if (!routeInfo?.view) return;
    const nextView = routeInfo.view;
    if (!isViewAllowed(nextView)) {
      setView(VIEWS.dashboard);
      setSelectedTournamentAdminId('');
      return;
    }
    setView(nextView);
    setSelectedTournamentAdminId(routeInfo.tournamentId || '');
  }, [routeInfo, isViewAllowed]);

  useEffect(() => {
    const { body, documentElement } = document;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: documentElement.style.overflow,
      htmlHeight: documentElement.style.height,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow || '',
      rootHeight: root?.style.height || '',
    };

    documentElement.style.overflow = 'auto';
    documentElement.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.position = 'static';
    body.style.height = 'auto';
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
    }

    return () => {
      documentElement.style.overflow = prev.htmlOverflow;
      documentElement.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isSuperadmin) {
      setStaffCoins(Number(user?.coins || 0));
    }
  }, [isSuperadmin, user?.coins]);

  useEffect(() => {
    let active = true;

    async function loadDirectory() {
      if (!shouldRefreshDirectory) return;
      setLoadingDirectory(true);
      try {
        const [usersRes, agentsRes] = await Promise.all([
          api.get(`/admin/users${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ''}`),
          isSuperadmin ? api.get('/admin/agents') : Promise.resolve({ data: { data: { agents: [] } } }),
        ]);

        if (!active) return;
        const nextUsers = usersRes.data?.data?.users || [];
        const nextAgents = agentsRes.data?.data?.agents || [];
        setUsers(nextUsers);
        setAgents(nextAgents);
        setAgentStats(agentsRes.data?.data?.stats || { totalAgents: nextAgents.length, activeAgents: 0, topAgent: null });
        setSelectedUserId((current) => {
          if (!current && nextUsers[0]?.id) return String(nextUsers[0].id);
          if (current && !nextUsers.some((entry) => String(entry.id) === String(current))) {
            return nextUsers[0] ? String(nextUsers[0].id) : '';
          }
          return current;
        });
        setCreateForm((prev) => ({
          ...prev,
          agentId: nextAgents.some((entry) => String(entry.id) === String(prev.agentId)) ? prev.agentId : '',
        }));
      } catch (err) {
        if (active) {
          setUsers([]);
          setAgents([]);
          setAgentStats({ totalAgents: 0, activeAgents: 0, topAgent: null });
          setError(err?.response?.data?.message || 'Failed to load dashboard data');
        }
      } finally {
        if (active) setLoadingDirectory(false);
      }
    }

    const timer = setTimeout(loadDirectory, search ? 220 : 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, isSuperadmin, shouldRefreshDirectory]);

  useEffect(() => {
    let active = true;

    async function loadDashboardStats() {
      if (view !== VIEWS.dashboard) return;
      setLoadingDashboard(true);
      try {
        const res = await api.get('/admin/dashboard/stats');
        if (active) {
          const nextStats = res.data?.data || EMPTY_DASHBOARD_STATS;
          setDashboardStats(nextStats);
          if (Number.isFinite(Number(nextStats.actorBalance))) {
            setStaffCoins(Number(nextStats.actorBalance));
          }
        }
      } catch (_err) {
        if (active) {
          setDashboardStats(EMPTY_DASHBOARD_STATS);
        }
      } finally {
        if (active) setLoadingDashboard(false);
      }
    }

    loadDashboardStats();
    return () => {
      active = false;
    };
  }, [isSuperadmin, view]);

  useEffect(() => {
    let active = true;

    async function loadDetails() {
      if (!selectedUserId) {
        setSelectedUser(null);
        setRecentTransactions([]);
        return;
      }

      try {
        const res = await api.get(`/admin/users/${selectedUserId}`);
        if (!active) return;
        setSelectedUser(res.data?.data?.user || null);
        setRecentTransactions(res.data?.data?.recentTransactions || []);
        setCommissionRateInput(String(res.data?.data?.user?.commissionRate ?? ''));
      } catch (err) {
        if (active) {
          setSelectedUser(null);
          setRecentTransactions([]);
          setCommissionRateInput('');
          setError(err?.response?.data?.message || 'Failed to load user details');
        }
      } finally {
        // no-op
      }
    }

    loadDetails();
    return () => {
      active = false;
    };
  }, [selectedUserId]);

  useEffect(() => {
    let active = true;

    async function loadActivities() {
      if (view !== VIEWS.activities) return;
      setLoadingActivities(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(activityPage));
        params.set('limit', '10');
        if (isSuperadmin) {
          params.set('scope', activityScope);
        }
        if (activitySearch.trim()) {
          params.set('q', activitySearch.trim());
        }
        const res = await api.get(`/admin/activities?${params.toString()}`);
        if (!active) return;
        const nextData = res.data?.data || {};
        setActivities(nextData.items || []);
        setActivitiesMeta({
          page: Number(nextData.page || 1),
          totalPages: Number(nextData.totalPages || 1),
          total: Number(nextData.total || 0),
          scope: nextData.scope || (isSuperadmin ? activityScope : 'customer'),
        });
      } catch (err) {
        if (!active) return;
        setActivities([]);
        setActivitiesMeta({ page: 1, totalPages: 1, total: 0, scope: isSuperadmin ? activityScope : 'customer' });
        setError(err?.response?.data?.message || 'Failed to load activities');
      } finally {
        if (active) setLoadingActivities(false);
      }
    }

    loadActivities();
    return () => {
      active = false;
    };
  }, [activityPage, activityScope, activitySearch, isSuperadmin, view]);

  useEffect(() => {
    let active = true;

    async function loadTransactions() {
      if (view !== VIEWS.transactions) return;
      setLoadingTransactions(true);
      try {
        const res = await api.get(`/wallet/activities?page=${transactionsPage}&limit=10`);
        if (!active) return;
        const nextData = res.data?.data || {};
        setMyTransactions(nextData.items || []);
        setMyTransactionsMeta({
          page: Number(nextData.page || 1),
          totalPages: Number(nextData.totalPages || 1),
          total: Number(nextData.total || 0),
        });
      } catch (err) {
        if (!active) return;
        setMyTransactions([]);
        setMyTransactionsMeta({ page: 1, totalPages: 1, total: 0 });
        setError(err?.response?.data?.message || 'Failed to load transactions');
      } finally {
        if (active) setLoadingTransactions(false);
      }
    }

    loadTransactions();
    return () => {
      active = false;
    };
  }, [transactionsPage, view]);

  useEffect(() => {
    let active = true;

    async function loadGames() {
      if (view !== VIEWS.games) return;
      setLoadingGames(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(gamesPage));
        params.set('limit', '10');
        if (gamesRange) {
          params.set('range', gamesRange);
        }
        if (gamesSearch.trim()) {
          params.set('q', gamesSearch.trim());
        }
        const res = await api.get(`/admin/games?${params.toString()}`);
        if (!active) return;
        const nextData = res.data?.data || {};
        setGames(nextData.items || []);
        setGamesMeta({
          page: Number(nextData.page || 1),
          totalPages: Number(nextData.totalPages || 1),
          total: Number(nextData.total || 0),
        });
      } catch (err) {
        if (!active) return;
        setGames([]);
        setGamesMeta({ page: 1, totalPages: 1, total: 0 });
        setError(err?.response?.data?.message || 'Failed to load games');
      } finally {
        if (active) setLoadingGames(false);
      }
    }

    loadGames();
    return () => {
      active = false;
    };
  }, [gamesPage, gamesRange, gamesSearch, view]);

  useEffect(() => {
    let active = true;

    async function loadEmailActivities() {
      if (!isSuperadmin) return;
      if (view !== VIEWS.emailActivities) return;
      setLoadingEmailActivities(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(emailActivitiesPage));
        params.set('limit', '10');
        if (emailActivitiesSearch.trim()) {
          params.set('q', emailActivitiesSearch.trim());
        }
        const res = await api.get(`/admin/email-activities?${params.toString()}`);
        if (!active) return;
        setEmailActivities(res.data?.data?.items || []);
        setEmailActivitiesMeta({
          page: Number(res.data?.data?.page || 1),
          totalPages: Number(res.data?.data?.totalPages || 1),
          total: Number(res.data?.data?.total || 0),
        });
      } catch (err) {
        if (active) {
          setEmailActivities([]);
          setEmailActivitiesMeta({ page: 1, totalPages: 1, total: 0 });
          setError(err?.response?.data?.message || 'Failed to load email activities');
        }
      } finally {
        if (active) setLoadingEmailActivities(false);
      }
    }

    loadEmailActivities();
    return () => {
      active = false;
    };
  }, [emailActivitiesPage, emailActivitiesSearch, isSuperadmin, view]);

  useEffect(() => {
    let active = true;

    async function loadOnlinePlayers() {
      if (view !== VIEWS.onlinePlayers) return;
      setLoadingOnlinePlayers(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(onlinePlayersPage));
        params.set('limit', '20');
        if (onlinePlayersSearch.trim()) {
          params.set('q', onlinePlayersSearch.trim());
        }
        const res = await api.get(`/admin/online-players?${params.toString()}`);
        if (!active) return;
        const nextData = res.data?.data || {};
        setOnlinePlayers(nextData.items || []);
        setOnlinePlayersMeta({
          page: Number(nextData.page || 1),
          totalPages: Number(nextData.totalPages || 1),
          total: Number(nextData.total || 0),
        });
      } catch (err) {
        if (!active) return;
        setOnlinePlayers([]);
        setOnlinePlayersMeta({ page: 1, totalPages: 1, total: 0 });
        setError(err?.response?.data?.message || 'Failed to load online players');
      } finally {
        if (active) setLoadingOnlinePlayers(false);
      }
    }

    loadOnlinePlayers();
    return () => {
      active = false;
    };
  }, [onlinePlayersPage, onlinePlayersSearch, view]);

  const stats = useMemo(() => {
    const visibleAgents = isSuperadmin ? agents.length : 0;
    const customerUsers = users.filter((entry) => entry.role === 'customer');
    const totalCustomers = customerUsers.length;
    const totalCoins = users.reduce((sum, entry) => sum + Number(entry.coins || 0), 0);
    const customersWithBalance = customerUsers.filter((entry) => Number(entry.coins || 0) > 0).length;
    const averageCustomerBalance = totalCustomers > 0
      ? Math.round(customerUsers.reduce((sum, entry) => sum + Number(entry.coins || 0), 0) / totalCustomers)
      : 0;
    return {
      visibleAgents,
      totalCustomers,
      totalCoins,
      customersWithBalance,
      averageCustomerBalance,
    };
  }, [agents.length, isSuperadmin, users]);

  async function refreshAfterMutation(preferredSelectedUserId = selectedUserId, options = {}) {
    const { silent = false } = options;
    try {
      const [usersRes, agentsRes] = await Promise.all([
        api.get(`/admin/users${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ''}`),
        isSuperadmin ? api.get('/admin/agents') : Promise.resolve({ data: { data: { agents: [] } } }),
      ]);

      const nextUsers = usersRes.data?.data?.users || [];
      const nextAgents = agentsRes.data?.data?.agents || [];
      setUsers(nextUsers);
      setAgents(nextAgents);
      setAgentStats(agentsRes.data?.data?.stats || { totalAgents: nextAgents.length, activeAgents: 0, topAgent: null });

      const nextSelectedId = preferredSelectedUserId && nextUsers.some((entry) => String(entry.id) === String(preferredSelectedUserId))
        ? String(preferredSelectedUserId)
        : (nextUsers[0] ? String(nextUsers[0].id) : '');

      setSelectedUserId(nextSelectedId);

      if (nextSelectedId) {
        const detailsRes = await api.get(`/admin/users/${nextSelectedId}`);
        setSelectedUser(detailsRes.data?.data?.user || null);
        setRecentTransactions(detailsRes.data?.data?.recentTransactions || []);
        setCommissionRateInput(String(detailsRes.data?.data?.user?.commissionRate ?? ''));
      } else {
        setSelectedUser(null);
        setRecentTransactions([]);
        setCommissionRateInput('');
      }
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.message || 'Failed to refresh admin data');
      }
      throw err;
    }
  }

  async function handleCreateCustomer(e) {
    e.preventDefault();
    setSubmittingCreate(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        name: createForm.name,
        username: createForm.username,
        email: createForm.email,
        password: createForm.password,
      };
      if (isSuperadmin && createForm.agentId) payload.agentId = createForm.agentId;

      const res = await api.post('/admin/users/customer', payload);
      const createdUserId = res.data?.data?.user?.id || '';
      setCreateForm((prev) => ({
        ...prev,
        name: '',
        username: '',
        email: '',
        password: '',
        agentId: '',
      }));
      setShowCustomerModal(false);
      setView(VIEWS.customers);
      await refreshAfterMutation(createdUserId);
      setMessage(res.data?.message || 'Customer created successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create customer');
    } finally {
      setSubmittingCreate(false);
    }
  }

  async function handleCreateAgent(e) {
    e.preventDefault();
    setSubmittingCreate(true);
    setMessage('');
    setError('');
    try {
      const res = await api.post('/admin/users/agent', {
        name: agentForm.name,
        username: agentForm.username,
        email: agentForm.email,
        password: agentForm.password,
        commissionRate: Number(agentForm.commissionRate),
      });
      const createdUserId = res.data?.data?.user?.id || '';
      setAgentForm({
        name: '',
        username: '',
        email: '',
        password: '',
        commissionRate: '0',
      });
      setShowAgentModal(false);
      setView(VIEWS.agents);
      await refreshAfterMutation(createdUserId);
      if (isSuperadmin) {
        const statsRes = await api.get('/admin/dashboard/stats');
        setDashboardStats(statsRes.data?.data || dashboardStats);
      }
      setMessage(res.data?.message || 'Agent created successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create agent');
    } finally {
      setSubmittingCreate(false);
    }
  }

  async function handleUpdateCommission(e) {
    e.preventDefault();
    const targetAgentId = view === VIEWS.agentDetails ? selectedAgentProfileId : selectedUserId;
    const targetAgent = view === VIEWS.agentDetails ? agentProfile : selectedUser;
    if (!targetAgentId || targetAgent?.role !== 'agent') return;

    setSubmittingCommission(true);
    setMessage('');
    setError('');
    try {
      const res = await api.patch(`/admin/users/${targetAgentId}/commission`, {
        commissionRate: Number(commissionRateInput),
      });
      await refreshAfterMutation(targetAgentId);
      if (view === VIEWS.agentDetails) {
        await loadAgentProfile(targetAgentId);
      }
      if (isSuperadmin) {
        const statsRes = await api.get('/admin/dashboard/stats');
        setDashboardStats(statsRes.data?.data || dashboardStats);
      }
      setMessage(res.data?.message || 'Agent commission updated successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update commission');
    } finally {
      setSubmittingCommission(false);
    }
  }

  async function loadAgentProfile(agentId, options = {}) {
    const { silent = false } = options;
    setLoadingAgentProfile(true);
    if (!silent) {
      setMessage('');
      setError('');
    }
    try {
      const [detailsRes, customersRes] = await Promise.all([
        api.get(`/admin/users/${agentId}`),
        api.get(`/admin/agents/${agentId}/customers`),
      ]);
      setAgentProfile(detailsRes.data?.data?.user || null);
      setAgentProfileCustomers(customersRes.data?.data?.customers || []);
      setAgentProfileCustomerPage(1);
      setRecentTransactions(detailsRes.data?.data?.recentTransactions || []);
      setCommissionRateInput(String(detailsRes.data?.data?.user?.commissionRate ?? ''));
      setSelectedAgentProfileId(String(agentId));
      setView(VIEWS.agentDetails);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.message || 'Failed to load agent details');
      }
      throw err;
    } finally {
      setLoadingAgentProfile(false);
    }
  }

  async function handleOpenAgentDetails(agent) {
    await loadAgentProfile(agent.id);
  }

  useEffect(() => {
    if (view !== VIEWS.agentDetails || !selectedAgentProfileId) return;
    loadAgentProfile(selectedAgentProfileId, { silent: true }).catch(() => {});
  }, [selectedAgentProfileId, view]);

  async function handleAgentWalletSubmit(e) {
    e.preventDefault();
    if (!selectedAgentProfileId) return;

    setSubmittingRecharge(true);
    setMessage('');
    setError('');
    try {
      const amount = Math.trunc(Number(agentWalletAmount));
      if (!Number.isFinite(amount) || amount < 1) {
        setError('Amount must be at least 1');
        return;
      }
      const signedCoins = agentWalletMode === 'withdraw' ? -amount : amount;
      const res = await api.post(`/admin/users/${selectedAgentProfileId}/coins`, {
        coins: signedCoins,
        note,
      });
      setAgentWalletAmount(String(amount));
      setNote('');
      setMessage(res.data?.message || 'Agent wallet updated successfully.');
      try {
        await refreshAfterMutation(selectedAgentProfileId, { silent: true });
        await loadAgentProfile(selectedAgentProfileId, { silent: true });
      } catch (refreshErr) {
        console.error('Agent wallet updated but refresh failed', refreshErr);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update agent wallet');
    } finally {
      setSubmittingRecharge(false);
    }
  }

  async function loadCustomerProfile(customerId, matchesPage = 1, options = {}) {
    const { silent = false } = options;
    setLoadingCustomerProfile(true);
    if (!silent) {
      setMessage('');
      setError('');
    }
    try {
      const detailsRes = await api.get(`/admin/users/${customerId}?matchesPage=${matchesPage}`);
      const nextCustomerProfile = detailsRes.data?.data?.user || null;
      setCustomerProfile(nextCustomerProfile);
      setCustomerAssignAgentId('');
      setCustomerTransactions(detailsRes.data?.data?.recentTransactions || []);
      setCustomerOnlineSessions(detailsRes.data?.data?.onlineSessions || []);
      setCustomerOnlineSessionsMeta(detailsRes.data?.data?.onlineSessionsMeta || { page: 1, totalPages: 1, total: 0, limit: 10 });
      setSelectedCustomerProfileId(String(customerId));
      setView(VIEWS.customerDetails);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.message || 'Failed to load customer details');
      }
      throw err;
    } finally {
      setLoadingCustomerProfile(false);
    }
  }

  async function handleOpenCustomerDetails(customer) {
    await loadCustomerProfile(customer.id);
  }

  useEffect(() => {
    if (view !== VIEWS.customerDetails || !selectedCustomerProfileId) return;
    loadCustomerProfile(selectedCustomerProfileId, 1, { silent: true }).catch(() => {});
  }, [selectedCustomerProfileId, view]);

  async function handleCustomerWalletSubmit(e) {
    e.preventDefault();
    if (!selectedCustomerProfileId) return;

    setSubmittingRecharge(true);
    setMessage('');
    setError('');
    try {
      const amount = Math.trunc(Number(customerWalletAmount));
      if (!Number.isFinite(amount) || amount < 1) {
        setError('Amount must be at least 1');
        return;
      }
      const signedCoins = customerWalletMode === 'withdraw' ? -amount : amount;
      const res = await api.post(`/admin/users/${selectedCustomerProfileId}/coins`, {
        coins: signedCoins,
        note,
      });
      if (!isSuperadmin && Number.isFinite(res.data?.data?.actorBalance)) {
        setStaffCoins(Number(res.data.data.actorBalance));
      }
      setCustomerWalletAmount(String(amount));
      setNote('');
      setMessage(res.data?.message || 'Customer wallet updated successfully.');
      try {
        await refreshAfterMutation(selectedCustomerProfileId, { silent: true });
        await loadCustomerProfile(selectedCustomerProfileId, 1, { silent: true });
      } catch (refreshErr) {
        console.error('Customer wallet updated but refresh failed', refreshErr);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update customer wallet');
    } finally {
      setSubmittingRecharge(false);
    }
  }

  async function handleAssignCustomerAgent(e) {
    e.preventDefault();
    if (!selectedCustomerProfileId || !customerAssignAgentId) {
      setError('Please select an agent');
      return;
    }

    setSubmittingCreate(true);
    setMessage('');
    setError('');
    try {
      const res = await api.patch(`/admin/users/${selectedCustomerProfileId}/assign-agent`, {
        agentId: customerAssignAgentId,
      });
      await refreshAfterMutation(selectedCustomerProfileId);
      await loadCustomerProfile(selectedCustomerProfileId);
      if (isSuperadmin) {
        const statsRes = await api.get('/admin/dashboard/stats');
        setDashboardStats(statsRes.data?.data || dashboardStats);
      }
      setMessage(res.data?.message || 'Customer assigned successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to assign customer');
    } finally {
      setSubmittingCreate(false);
    }
  }

  const filteredAgents = useMemo(() => {
    const needle = agentSearch.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter((agent) =>
      [agent.name, agent.username, agent.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [agentSearch, agents]);

  const customerUsers = useMemo(() => users.filter((entry) => entry.role === 'customer'), [users]);
  const paginatedAgents = useMemo(() => {
    const start = (agentListPage - 1) * LIST_PAGE_SIZE;
    return filteredAgents.slice(start, start + LIST_PAGE_SIZE);
  }, [agentListPage, filteredAgents]);
  const paginatedCustomers = useMemo(() => {
    const start = (customerListPage - 1) * LIST_PAGE_SIZE;
    return customerUsers.slice(start, start + LIST_PAGE_SIZE);
  }, [customerListPage, customerUsers]);
  const paginatedAgentProfileCustomers = useMemo(() => {
    const start = (agentProfileCustomerPage - 1) * LIST_PAGE_SIZE;
    return agentProfileCustomers.slice(start, start + LIST_PAGE_SIZE);
  }, [agentProfileCustomerPage, agentProfileCustomers]);
  const totalAgentPages = Math.max(1, Math.ceil(filteredAgents.length / LIST_PAGE_SIZE));
  const totalAgentProfileCustomerPages = Math.max(1, Math.ceil(agentProfileCustomers.length / LIST_PAGE_SIZE));
  const totalCustomerPages = Math.max(1, Math.ceil(customerUsers.length / LIST_PAGE_SIZE));
  const visibleMyTransactions = useMemo(() => {
    if (transactionsFilter === 'deposit') return myTransactions.filter((entry) => Number(entry.amount || 0) > 0);
    if (transactionsFilter === 'withdrawal') return myTransactions.filter((entry) => Number(entry.amount || 0) < 0);
    return myTransactions;
  }, [myTransactions, transactionsFilter]);

  const isTablet = viewportWidth < 1120;
  const isMobile = viewportWidth < 760;

  useEffect(() => {
    setAgentListPage(1);
  }, [agentSearch]);

  useEffect(() => {
    setCustomerListPage(1);
  }, [search]);

  useEffect(() => {
    setGamesPage(1);
  }, [gamesSearch, gamesRange]);

  useEffect(() => {
    setOnlinePlayersPage(1);
  }, [onlinePlayersSearch]);

  useEffect(() => {
    if (agentListPage > totalAgentPages) {
      setAgentListPage(totalAgentPages);
    }
  }, [agentListPage, totalAgentPages]);

  useEffect(() => {
    if (customerListPage > totalCustomerPages) {
      setCustomerListPage(totalCustomerPages);
    }
  }, [customerListPage, totalCustomerPages]);

  useEffect(() => {
    if (agentProfileCustomerPage > totalAgentProfileCustomerPages) {
      setAgentProfileCustomerPage(totalAgentProfileCustomerPages);
    }
  }, [agentProfileCustomerPage, totalAgentProfileCustomerPages]);

  const sidebarItems = [
    { id: VIEWS.dashboard, label: 'Dashboard' },
    ...(isSuperadmin ? [{ id: VIEWS.agents, label: 'Agents' }] : []),
    { id: VIEWS.customers, label: 'Customers' },
    { id: VIEWS.tournaments, label: 'Tournaments' },
    { id: VIEWS.transactions, label: 'Transactions' },
    { id: VIEWS.games, label: 'Games' },
    { id: VIEWS.onlinePlayers, label: 'Online Matches' },
    { id: VIEWS.activities, label: 'Activities' },
    { id: VIEWS.support, label: 'Helpdesk' },
    ...(isSuperadmin ? [{ id: VIEWS.emailActivities, label: 'Emails' }] : []),
  ];

  function handleAdminViewChange(nextView) {
    if (!isViewAllowed(nextView)) return;
    setView(nextView);
    if (nextView !== VIEWS.tournamentDetails) {
      setSelectedTournamentAdminId('');
    }
    if (typeof onNavigate === 'function') {
      onNavigate({
        view: nextView,
        tournamentId: nextView === VIEWS.tournamentDetails ? selectedTournamentAdminId : '',
      });
    }
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.shell, gridTemplateColumns: isTablet ? '1fr' : '280px minmax(0, 1fr)' }}>
        <aside style={{ ...styles.sidebar, padding: isMobile ? 18 : 22 }}>
          <div>
            <div style={styles.kicker}>{isSuperadmin ? 'Superadmin Console' : 'Agent Console'}</div>
            <div style={styles.staffName}>{user?.name}</div>
            <div style={styles.staffMeta}>{user?.email}</div>
          </div>

          <div style={styles.balanceCard}>
            <div style={styles.balanceLabel}>{isSuperadmin ? 'Company Wallet' : 'Agent Balance'}</div>
            <div style={styles.balanceValue}>{formatNumber(staffCoins)}</div>
            <div style={styles.balanceCaption}>coins available</div>
          </div>

          <div style={styles.navList}>
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleAdminViewChange(item.id)}
                style={{ ...styles.navBtn, ...(view === item.id ? styles.navBtnActive : {}) }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button type="button" onClick={onLogout} style={styles.logoutBtn}>Logout</button>
        </aside>

        <main style={styles.mainArea}>
          {view === VIEWS.dashboard && (loadingDashboard || loadingDirectory) && (
            <PanelLoader
              title="Refreshing dashboard"
              description="Pulling the latest balances, player counts, and live game activity."
            />
          )}

          {view === VIEWS.dashboard && !(loadingDashboard || loadingDirectory) && (
            <section style={styles.dashboardStatsGrid}>
              {isSuperadmin
                ? <StatCard label="Agents" value={formatNumber(dashboardStats.cards?.agents || 0)} tone="cream" />
                : <StatCard label="Customers" value={formatNumber(dashboardStats.cards?.customers || stats.totalCustomers || 0)} tone="cream" />}
              <StatCard
                label={isSuperadmin ? 'Customers' : 'Funded Players'}
                value={formatNumber(isSuperadmin ? (dashboardStats.cards?.customers || 0) : (dashboardStats.cards?.fundedPlayers || stats.customersWithBalance || 0))}
                tone="amber"
              />
            </section>
          )}

          {view === VIEWS.support && (
            <section style={{ display: 'grid', gap: 14 }}>
              <div style={styles.sectionHeader}>
                <div>
                  <div style={styles.sectionTitle}>Helpdesk</div>
                  <div style={styles.sectionSubtitle}>Chat with players and guests.</div>
                </div>
              </div>
              <AdminSupportSection token={localStorage.getItem('bg_admin_token') || ''} />
            </section>
          )}

          {isSuperadmin && view === VIEWS.dashboard && !(loadingDashboard || loadingDirectory) && (
            <section style={styles.panel}>
              <div style={styles.panelTitle}>Realtime Stats</div>
              <div style={styles.panelMeta}>Live tabs for current platform activity.</div>
              <div style={styles.realtimeTabs}>
                <MiniStat label="Games Today" value={dashboardStats.realtime?.gamesToday || 0} />
                <MiniStat label="Games This Week" value={dashboardStats.realtime?.gamesThisWeek || 0} />
              </div>
            </section>
          )}

          {!isSuperadmin && view === VIEWS.dashboard && !(loadingDashboard || loadingDirectory) && (
            <>
              <section style={styles.panel}>
                <div style={styles.panelTitle}>Realtime Stats</div>
                <div style={styles.panelMeta}>Today and weekly activity across your customer network.</div>
                <div style={styles.realtimeTabs}>
                  <MiniStat label="Games Today" value={dashboardStats.realtime?.gamesToday || 0} />
                  <MiniStat label="Games This Week" value={dashboardStats.realtime?.gamesThisWeek || 0} />
                </div>
              </section>
            </>
          )}

          {(message || error) && (
            <div style={{ ...styles.feedback, ...(error ? styles.feedbackError : styles.feedbackSuccess) }}>
              {error || message}
            </div>
          )}

          {view === VIEWS.agents && isSuperadmin && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Agents</div>
                  <div style={styles.panelMeta}>
                    Search agents, review commission, and inspect each agent customer list.
                  </div>
                </div>
                <button type="button" onClick={() => setShowAgentModal(true)} style={styles.primaryBtn}>
                  Add Agent
                </button>
              </div>

              <div style={{ ...styles.statsGrid, ...styles.compactStatsGrid }}>
                <StatCard label="Total Agents" value={formatNumber(agentStats.totalAgents || 0)} tone="cream" compact />
                <StatCard label="Active Agents" value={formatNumber(agentStats.activeAgents || 0)} tone="rose" compact />
                <StatCard
                  label="Most Customers"
                  value={agentStats.topAgent ? `${agentStats.topAgent.name} (${formatNumber(agentStats.topAgent.customerCount)})` : 'No agents'}
                  tone="amber"
                  compact
                />
              </div>

              <div style={styles.searchRow}>
                <input
                  type="text"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  placeholder="Search agent by name, username, or email"
                  style={styles.input}
                />
              </div>

              {loadingDirectory ? (
                <PanelLoader
                  compact
                  title="Refreshing agents"
                  description="Loading the latest balances, commission rates, and assigned customer counts."
                />
              ) : (
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 640 : 840 }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Agent</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Commission</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Customers</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Balance</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Created</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'center' }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAgents.map((agent) => (
                      <tr key={agent.id}>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                          <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{agent.username}</div>
                        </td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}><span style={styles.tableValueText}>{Number(agent.commissionRate || 0)}%</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}><span style={styles.tableValueText}>{formatNumber(agent.customerCount || 0)}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}><span style={styles.tableValueText}>{formatNumber(agent.coins || 0)}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}><span style={styles.tableValueText}>{new Date(agent.createdAt).toLocaleDateString()}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenAgentDetails(agent)}
                            style={styles.listIconBtn}
                            title="View agent details"
                          >
                            <EyeIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!filteredAgents.length && <div style={styles.empty}>No agents found.</div>}
              </div>
              )}
              {!loadingDirectory && filteredAgents.length > 0 && (
                <div style={styles.paginationRow}>
                  <button
                    type="button"
                    onClick={() => setAgentListPage((current) => Math.max(1, current - 1))}
                    disabled={agentListPage <= 1}
                    style={{ ...styles.quickBtn, ...(agentListPage <= 1 ? styles.quickBtnDisabled : {}) }}
                  >
                    Previous
                  </button>
                  <div style={styles.paginationLabel}>
                    Page {formatNumber(agentListPage)} of {formatNumber(totalAgentPages)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setAgentListPage((current) => Math.min(totalAgentPages, current + 1))}
                    disabled={agentListPage >= totalAgentPages}
                    style={{ ...styles.quickBtn, ...(agentListPage >= totalAgentPages ? styles.quickBtnDisabled : {}) }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}

          {view === VIEWS.agentDetails && isSuperadmin && (
            <section style={styles.panel}>
              <div style={styles.listHeader}>
                <div>
                  <div style={styles.panelTitle}>Agent Details</div>
                  <div style={styles.panelMeta}>Complete agent profile, wallet actions, transactions, and players.</div>
                </div>
                <button type="button" onClick={() => setView(VIEWS.agents)} style={styles.modalCloseBtn}>Back To Agents</button>
              </div>

              {loadingAgentProfile && (
                <PanelLoader
                  compact
                  title="Refreshing agent profile"
                  description="Loading wallet controls, transactions, and assigned players."
                />
              )}
              {!loadingAgentProfile && agentProfile && (
                <>
                  <div style={{ ...styles.detailHero, gap: 12 }}>
                    <div>
                      <div style={styles.detailRole}>agent</div>
                      <div style={styles.detailName}>{agentProfile.name}</div>
                      <div style={styles.detailMeta}>@{agentProfile.username} - {agentProfile.email}</div>
                    </div>
                    <div style={styles.detailBalance}>
                      <div style={styles.detailBalanceLabel}>Agent Wallet</div>
                      <div style={styles.detailBalanceValue}>{formatNumber(agentProfile.coins)}</div>
                    </div>
                  </div>

                  <div style={styles.infoGrid}>
                    <Info label="Commission" value={`${Number(agentProfile.commissionRate || 0)}%`} />
                    <Info label="Players" value={formatNumber(agentProfileCustomers.length)} />
                    <Info label="Created" value={new Date(agentProfile.createdAt).toLocaleString()} />
                    <Info label="Updated" value={new Date(agentProfile.updatedAt).toLocaleString()} />
                  </div>

                  <div style={{ ...styles.agentDetailGrid, gridTemplateColumns: isMobile ? '1fr' : styles.agentDetailGrid.gridTemplateColumns }}>
                    <form onSubmit={handleAgentWalletSubmit} style={styles.rechargeCard}>
                      <div>
                        <div style={styles.panelTitle}>Wallet Control</div>
                        <div style={styles.panelMeta}>Use this to add coins or withdraw coins from the agent wallet.</div>
                      </div>

                      <div style={styles.toggleRow}>
                        <button
                          type="button"
                          onClick={() => setAgentWalletMode('credit')}
                          style={{ ...styles.quickBtn, ...(agentWalletMode === 'credit' ? styles.quickBtnActive : {}) }}
                        >
                          Add Coins
                        </button>
                        <button
                          type="button"
                          onClick={() => setAgentWalletMode('withdraw')}
                          style={{ ...styles.quickBtn, ...(agentWalletMode === 'withdraw' ? styles.quickBtnActive : {}) }}
                        >
                          Withdraw Coins
                        </button>
                      </div>

                      <label style={styles.label}>
                        Amount
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={agentWalletAmount}
                          onChange={(e) => setAgentWalletAmount(e.target.value)}
                          style={styles.input}
                        />
                      </label>

                      <label style={styles.label}>
                        Note
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for wallet adjustment" style={styles.input} />
                      </label>

                      <button type="submit" disabled={submittingRecharge} style={styles.primaryBtn}>
                        {submittingRecharge ? 'Processing...' : agentWalletMode === 'withdraw' ? 'Withdraw Coins' : 'Add Coins'}
                      </button>
                    </form>

                    <form onSubmit={handleUpdateCommission} style={styles.rechargeCard}>
                      <div>
                        <div style={styles.panelTitle}>Commission Control</div>
                        <div style={styles.panelMeta}>Update the commission percentage for this agent.</div>
                      </div>

                      <label style={styles.label}>
                        Commission Rate (%)
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={commissionRateInput}
                          onChange={(e) => setCommissionRateInput(e.target.value)}
                          style={styles.input}
                        />
                      </label>

                      <button type="submit" disabled={submittingCommission} style={styles.primaryBtn}>
                        {submittingCommission ? 'Updating...' : 'Update Commission'}
                      </button>
                    </form>
                  </div>

                  <div style={{ ...styles.agentDetailGrid, gridTemplateColumns: isMobile ? '1fr' : styles.agentDetailGrid.gridTemplateColumns }}>
                    <div style={styles.detailSectionCard}>
                      <div style={styles.detailSectionHeader}>
                        <div>
                          <div style={styles.panelTitle}>Transactions</div>
                          <div style={styles.panelMeta}>Latest wallet and match movements for this agent.</div>
                        </div>
                        <div style={styles.detailPill}>{formatNumber(recentTransactions.length)} records</div>
                      </div>

                      <div style={styles.transactionsList}>
                        {recentTransactions.map((tx) => (
                          <div key={tx._id} style={styles.transactionRow}>
                            <div>
                              <div style={styles.transactionType}>{humanizeTxType(tx.type)}</div>
                              <div style={styles.transactionMeta}>{new Date(tx.createdAt).toLocaleString()}</div>
                              {getTransactionActor(tx) && <div style={styles.transactionMeta}>{getTransactionActor(tx)}</div>}
                            </div>
                            <div style={{ ...styles.transactionAmount, color: tx.amount >= 0 ? '#9b623f' : '#b86553' }}>
                              {tx.amount >= 0 ? '+' : ''}{formatNumber(tx.amount)}
                            </div>
                          </div>
                        ))}
                        {recentTransactions.length === 0 && (
                          <div style={styles.detailEmptyState}>
                            <div style={styles.detailEmptyTitle}>No transactions yet</div>
                            <div style={styles.detailEmptyText}>Wallet credits, withdrawals, commissions, and match-related entries will appear here.</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={styles.detailSectionCard}>
                      <div style={styles.detailSectionHeader}>
                        <div>
                          <div style={styles.panelTitle}>Players</div>
                          <div style={styles.panelMeta}>Customers currently assigned under this agent.</div>
                        </div>
                        <div style={styles.detailPill}>{formatNumber(agentProfileCustomers.length)} players</div>
                      </div>

                      {agentProfileCustomers.length > 0 && (
                        <div style={{ ...styles.tableWrap, ...styles.playerTableWrap }}>
                          <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 680 : 740 }}>
                            <thead>
                              <tr>
                                <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Player</th>
                                <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Username</th>
                                <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Balance</th>
                                <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedAgentProfileCustomers.map((customer) => (
                                <tr key={customer.id}>
                                  <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                    <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{customer.name}</div>
                                    <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{customer.email}</div>
                                  </td>
                                  <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                    <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>@{customer.username}</div>
                                  </td>
                                  <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}>{formatNumber(customer.coins || 0)}</td>
                                  <td style={{ ...styles.tableCell, ...styles.listTableCell }}>{new Date(customer.createdAt).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {agentProfileCustomers.length > 0 && (
                        <div style={styles.paginationRow}>
                          <button
                            type="button"
                            onClick={() => setAgentProfileCustomerPage((current) => Math.max(1, current - 1))}
                            disabled={agentProfileCustomerPage <= 1}
                            style={{ ...styles.quickBtn, ...(agentProfileCustomerPage <= 1 ? styles.quickBtnDisabled : {}) }}
                          >
                            Previous
                          </button>
                          <div style={styles.paginationLabel}>
                            Page {formatNumber(agentProfileCustomerPage)} of {formatNumber(totalAgentProfileCustomerPages)}
                          </div>
                          <button
                            type="button"
                            onClick={() => setAgentProfileCustomerPage((current) => Math.min(totalAgentProfileCustomerPages, current + 1))}
                            disabled={agentProfileCustomerPage >= totalAgentProfileCustomerPages}
                            style={{ ...styles.quickBtn, ...(agentProfileCustomerPage >= totalAgentProfileCustomerPages ? styles.quickBtnDisabled : {}) }}
                          >
                            Next
                          </button>
                        </div>
                      )}

                      {agentProfileCustomers.length === 0 && (
                        <div style={styles.detailEmptyState}>
                          <div style={styles.detailEmptyTitle}>No players assigned</div>
                          <div style={styles.detailEmptyText}>Create customers and assign them to this agent to see them listed here.</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {view === VIEWS.customers && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Customers</div>
                  <div style={styles.panelMeta}>
                    {isSuperadmin
                      ? 'Browse all customers, assign new ones, and open each customer wallet details.'
                      : 'Manage your customer list, add new customers, and open wallet details.'}
                  </div>
                </div>
                <button type="button" onClick={() => setShowCustomerModal(true)} style={styles.primaryBtn}>
                  Add Customer
                </button>
              </div>

              <div style={styles.searchRow}>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer by name, username, or email"
                  style={styles.input}
                />
              </div>

              {loadingDirectory ? (
                <PanelLoader
                  compact
                  title="Refreshing customers"
                  description="Loading the latest player balances, assignment details, and signup data."
                />
              ) : (
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 640 : 840 }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Customer</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Agent</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Balance</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Created</th>
                      <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'center' }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCustomers.map((customer) => (
                      <tr key={customer.id}>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                          <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{customer.username}</div>
                        </td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}><span style={styles.tableValueText}>{customer.parentAgentName || (isSuperadmin ? 'Not assigned' : user?.name || '-')}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}><span style={styles.tableValueText}>{formatNumber(customer.coins || 0)}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell }}><span style={styles.tableValueText}>{new Date(customer.createdAt).toLocaleDateString()}</span></td>
                        <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'center' }}>
                          <button type="button" onClick={() => handleOpenCustomerDetails(customer)} style={styles.listIconBtn} title="View customer details">
                            <EyeIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!customerUsers.length && <div style={styles.empty}>No customers found.</div>}
              </div>
              )}
              {!loadingDirectory && customerUsers.length > 0 && (
                <div style={styles.paginationRow}>
                  <button
                    type="button"
                    onClick={() => setCustomerListPage((current) => Math.max(1, current - 1))}
                    disabled={customerListPage <= 1}
                    style={{ ...styles.quickBtn, ...(customerListPage <= 1 ? styles.quickBtnDisabled : {}) }}
                  >
                    Previous
                  </button>
                  <div style={styles.paginationLabel}>
                    Page {formatNumber(customerListPage)} of {formatNumber(totalCustomerPages)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCustomerListPage((current) => Math.min(totalCustomerPages, current + 1))}
                    disabled={customerListPage >= totalCustomerPages}
                    style={{ ...styles.quickBtn, ...(customerListPage >= totalCustomerPages ? styles.quickBtnDisabled : {}) }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}

          {view === VIEWS.customerDetails && (
            <section style={styles.panel}>
              <div style={styles.listHeader}>
                <div>
                  <div style={styles.panelTitle}>Player Profile</div>
                  <div style={styles.panelMeta}>Online-only session summary, wallet controls, and linked account details.</div>
                </div>
                <button type="button" onClick={() => setView(VIEWS.customers)} style={styles.modalCloseBtn}>Back To Customers</button>
              </div>

              {loadingCustomerProfile && (
                <PanelLoader
                  compact
                  title="Refreshing player profile"
                  description="Loading wallet info, online sessions, and recent transactions."
                />
              )}
              {!loadingCustomerProfile && customerProfile && (
                <>
                  <div style={styles.detailHero}>
                    <div>
                      <div style={styles.detailRole}>customer</div>
                      <div style={styles.detailName}>{customerProfile.name}</div>
                      <div style={styles.detailMeta}>@{customerProfile.username} - {customerProfile.email}</div>
                    </div>
                    <div style={{ ...styles.detailBalance, padding: 14, minWidth: isMobile ? '100%' : 210 }}>
                      <div style={styles.detailBalanceLabel}>Customer Wallet</div>
                      <div style={{ ...styles.detailBalanceValue, fontSize: 28 }}>{formatNumber(customerProfile.coins)}</div>
                    </div>
                  </div>

                  <div style={{ ...styles.infoGrid, gap: 10, gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    <Info label="Assigned Agent" value={customerProfile.parentAgentName || (isSuperadmin ? 'Not assigned' : user?.name || '-')} />
                    <Info label={isSuperadmin ? 'Company Wallet' : 'Agent Wallet'} value={formatNumber(staffCoins)} />
                    <Info label="Online Sessions" value={formatNumber(customerOnlineSessionsMeta.total || 0)} />
                    <Info label="Created" value={new Date(customerProfile.createdAt).toLocaleString()} />
                    <Info label="Updated" value={new Date(customerProfile.updatedAt).toLocaleString()} />
                  </div>

                  <div style={{ ...styles.agentDetailGrid, gap: 14, gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 330px) minmax(0, 1fr)' }}>
                    <form onSubmit={handleCustomerWalletSubmit} style={{ ...styles.rechargeCard, padding: 14, gap: 10 }}>
                      <div>
                        <div style={styles.panelTitle}>Wallet Control</div>
                        <div style={{ ...styles.panelMeta, fontSize: 13 }}>
                          {isSuperadmin
                            ? 'Deposit or withdraw points from this customer wallet.'
                            : 'Deposit or withdraw points. Your agent wallet will decrease or increase by the same amount.'}
                        </div>
                      </div>

                      <div style={styles.toggleRow}>
                        <button
                          type="button"
                          onClick={() => setCustomerWalletMode('credit')}
                          style={{ ...styles.quickBtn, ...(customerWalletMode === 'credit' ? styles.quickBtnActive : {}) }}
                        >
                          Deposit
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomerWalletMode('withdraw')}
                          style={{ ...styles.quickBtn, ...(customerWalletMode === 'withdraw' ? styles.quickBtnActive : {}) }}
                        >
                          Withdraw
                        </button>
                      </div>

                      <label style={styles.label}>
                        Amount
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={customerWalletAmount}
                          onChange={(e) => setCustomerWalletAmount(e.target.value)}
                          style={styles.input}
                        />
                      </label>

                      <label style={styles.label}>
                        Note
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for wallet update" style={styles.input} />
                      </label>

                      {!isSuperadmin && (
                        <div style={styles.transferHint}>
                          Current agent wallet: {formatNumber(staffCoins)} coins
                        </div>
                      )}

                      <button type="submit" disabled={submittingRecharge} style={styles.primaryBtn}>
                        {submittingRecharge ? 'Processing...' : customerWalletMode === 'withdraw' ? 'Withdraw Points' : 'Deposit Points'}
                      </button>
                    </form>

                    {isSuperadmin && !customerProfile.parentAgentId && (
                      <form onSubmit={handleAssignCustomerAgent} style={{ ...styles.detailSectionCard, padding: 16, gap: 10 }}>
                        <div style={styles.detailSectionHeader}>
                          <div>
                            <div style={styles.panelTitle}>Assign Agent</div>
                            <div style={styles.panelMeta}>Assign this unassigned customer to an agent.</div>
                          </div>
                          <div style={styles.detailPill}>Unassigned</div>
                        </div>

                        <label style={styles.label}>
                          Select Agent
                          <select
                            value={customerAssignAgentId}
                            onChange={(e) => setCustomerAssignAgentId(e.target.value)}
                            style={styles.input}
                          >
                            <option value="">Select agent</option>
                            {agents.map((agent) => (
                              <option key={agent.id} value={agent.id}>
                                {agent.username}
                              </option>
                            ))}
                          </select>
                        </label>

                        <button type="submit" disabled={submittingCreate || !customerAssignAgentId} style={styles.primaryBtn}>
                          {submittingCreate ? 'Assigning...' : 'Assign To Agent'}
                        </button>
                      </form>
                    )}

                    <div style={{ ...styles.detailSectionCard, padding: 16, gap: 12 }}>
                      <div style={styles.detailSectionHeader}>
                        <div>
                          <div style={styles.panelTitle}>Online Match Sessions</div>
                          <div style={{ ...styles.panelMeta, fontSize: 13 }}>Recent online series only.</div>
                        </div>
                        <div style={styles.detailPill}>{formatNumber(customerOnlineSessionsMeta.total || 0)} rows</div>
                      </div>

                      {customerOnlineSessions.length > 0 ? (
                        <>
                          <div style={{ ...styles.tableWrap, ...styles.playerTableWrap, maxHeight: 'min(52vh, 540px)' }}>
                            <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 760 : 860 }}>
                              <thead>
                                <tr>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Opponent</th>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Type</th>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Score</th>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Win/Loss</th>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Stake</th>
                                  <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Date Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customerOnlineSessions.map((session) => (
                                  <tr key={session.id}>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                      <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{session.opponentName || '-'}</div>
                                    </td>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                      <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{session.typeLabel || '-'}</div>
                                    </td>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                      <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{session.scoreLine || '-'}</div>
                                    </td>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                      <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{session.result || '-'}</div>
                                    </td>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}>
                                      <span style={styles.tableValueText}>{formatNumber(session.stake)}</span>
                                    </td>
                                    <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                                      <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>
                                        {session.dateTime ? new Date(session.dateTime).toLocaleString() : '-'}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div style={styles.paginationRow}>
                            <button
                              type="button"
                              onClick={() => loadCustomerProfile(selectedCustomerProfileId, Math.max(1, customerOnlineSessionsMeta.page - 1))}
                              disabled={loadingCustomerProfile || customerOnlineSessionsMeta.page <= 1}
                              style={{ ...styles.quickBtn, ...(loadingCustomerProfile || customerOnlineSessionsMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                            >
                              Previous
                            </button>
                            <div style={styles.paginationLabel}>
                              Page {formatNumber(customerOnlineSessionsMeta.page)} of {formatNumber(customerOnlineSessionsMeta.totalPages)}
                            </div>
                            <button
                              type="button"
                              onClick={() => loadCustomerProfile(selectedCustomerProfileId, Math.min(customerOnlineSessionsMeta.totalPages, customerOnlineSessionsMeta.page + 1))}
                              disabled={loadingCustomerProfile || customerOnlineSessionsMeta.page >= customerOnlineSessionsMeta.totalPages}
                              style={{ ...styles.quickBtn, ...(loadingCustomerProfile || customerOnlineSessionsMeta.page >= customerOnlineSessionsMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                            >
                              Next
                            </button>
                          </div>
                        </>
                      ) : (
                        <div style={styles.detailEmptyState}>
                          <div style={styles.detailEmptyTitle}>No online sessions yet</div>
                          <div style={styles.detailEmptyText}>Opponent name, score line, result, stake, and time will appear here once the player finishes online matches.</div>
                        </div>
                      )}
                    </div>

                    <div style={{ ...styles.detailSectionCard, padding: 14, gap: 10, gridColumn: '1 / -1' }}>
                      <div style={styles.detailSectionHeader}>
                        <div>
                          <div style={styles.panelTitle}>Transactions</div>
                          <div style={{ ...styles.panelMeta, fontSize: 12 }}>Recent wallet transactions for this customer.</div>
                        </div>
                        <div style={styles.detailPill}>{formatNumber(customerTransactions.length)} records</div>
                      </div>

                      <div style={styles.transactionsList}>
                        {customerTransactions.map((tx) => (
                          <div key={tx._id} style={styles.transactionRow}>
                            <div>
                              <div style={styles.transactionType}>{humanizeTxType(tx.type)}</div>
                              <div style={styles.transactionMeta}>{new Date(tx.createdAt).toLocaleString()}</div>
                              {getTransactionActor(tx) && <div style={styles.transactionMeta}>{getTransactionActor(tx)}</div>}
                            </div>
                            <div style={{ ...styles.transactionAmount, color: tx.amount >= 0 ? '#9b623f' : '#b86553' }}>
                              {tx.amount >= 0 ? '+' : ''}{formatNumber(tx.amount)}
                            </div>
                          </div>
                        ))}
                        {!customerTransactions.length && <div style={styles.empty}>No transactions found.</div>}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {view === VIEWS.tournaments && (
            <TournamentAdminPanel
              user={user}
              routeInfo={routeInfo}
              onNavigate={onNavigate}
              readOnly={!isSuperadmin}
              onOpenTournamentDetails={(tournamentId) => {
                setSelectedTournamentAdminId(String(tournamentId));
                setView(VIEWS.tournamentDetails);
                if (typeof onNavigate === 'function') {
                  onNavigate({
                    view: VIEWS.tournamentDetails,
                    tournamentId: String(tournamentId),
                  });
                }
              }}
            />
          )}

          {view === VIEWS.tournamentDetails && (
            <TournamentAdminPanel
              user={user}
              initialTournamentId={selectedTournamentAdminId}
              detailOnly
              routeInfo={routeInfo}
              onNavigate={onNavigate}
              readOnly={!isSuperadmin}
              onBack={() => {
                setSelectedTournamentAdminId('');
                setView(VIEWS.tournaments);
                if (typeof onNavigate === 'function') {
                  onNavigate({
                    view: VIEWS.tournaments,
                    tournamentId: '',
                  });
                }
              }}
            />
          )}

          {view === VIEWS.transactions && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Transactions</div>
                  <div style={styles.panelMeta}>Your own coin wallet history, including credits, debits, fees, and match settlements.</div>
                </div>
                <div style={styles.detailPill}>{formatNumber(myTransactionsMeta.total)} records</div>
              </div>

              <div style={{ ...styles.listHeader, justifyContent: 'flex-end' }}>
                <select
                  value={transactionsFilter}
                  onChange={(e) => setTransactionsFilter(e.target.value)}
                  style={{ ...styles.input, width: isMobile ? '100%' : 180, padding: '10px 12px' }}
                >
                  <option value="all">All</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                </select>
              </div>

              {loadingTransactions && (
                <PanelLoader
                  compact
                  title="Refreshing transactions"
                  description="Getting the latest wallet credits, debits, and balance changes."
                />
              )}
              {!loadingTransactions && !visibleMyTransactions.length && (
                <div style={styles.detailEmptyState}>
                  <div style={styles.detailEmptyTitle}>No transactions found</div>
                  <div style={styles.detailEmptyText}>Coin credits, debits, fees, and match-related entries will appear here.</div>
                </div>
              )}

              {!loadingTransactions && visibleMyTransactions.length > 0 && (
                <div style={styles.tableWrap}>
                  <table style={{ ...styles.table, ...styles.activityTable, minWidth: isMobile ? 720 : 880 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Type</th>
                        {!isSuperadmin && <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Details</th>}
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead, textAlign: 'right' }}>Balance</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleMyTransactions.map((entry) => (
                        <tr key={entry._id}>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.activityTablePrimary }}>{humanizeTxType(entry.type)}</div>
                          </td>
                          {!isSuperadmin && (
                            <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                              <div style={{ ...styles.tableSecondary, ...styles.activityTableSecondary }}>
                                {getTransactionActor(entry) || 'No extra details'}
                              </div>
                            </td>
                          )}
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell, textAlign: 'right' }}>
                            <span style={{ ...styles.tableValueText, color: entry.amount >= 0 ? '#9b623f' : '#b86553' }}>
                              {entry.amount >= 0 ? '+' : ''}{formatNumber(entry.amount)}
                            </span>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell, textAlign: 'right' }}>
                            <span style={styles.tableValueText}>{formatNumber(entry.balanceAfter)}</span>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.activityTableSecondary }}>
                              {new Date(entry.createdAt).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={styles.paginationRow}>
                <button
                  type="button"
                  onClick={() => setTransactionsPage((current) => Math.max(1, current - 1))}
                  disabled={loadingTransactions || myTransactionsMeta.page <= 1}
                  style={{ ...styles.quickBtn, ...(loadingTransactions || myTransactionsMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                >
                  Previous
                </button>
                <div style={styles.paginationLabel}>
                  Page {formatNumber(myTransactionsMeta.page)} of {formatNumber(myTransactionsMeta.totalPages)}
                </div>
                <button
                  type="button"
                  onClick={() => setTransactionsPage((current) => Math.min(myTransactionsMeta.totalPages, current + 1))}
                  disabled={loadingTransactions || myTransactionsMeta.page >= myTransactionsMeta.totalPages}
                  style={{ ...styles.quickBtn, ...(loadingTransactions || myTransactionsMeta.page >= myTransactionsMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {view === VIEWS.activities && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Activities</div>
                  
                </div>
                <div style={styles.detailPill}>{formatNumber(activitiesMeta.total)} records</div>
              </div>

              <div style={styles.searchRow}>
                <input
                  type="text"
                  value={activitySearch}
                  onChange={(e) => {
                    setActivitySearch(e.target.value);
                    setActivityPage(1);
                  }}
                  placeholder={`Search ${isSuperadmin ? activitiesMeta.scope : 'customer'} by name, username, or email`}
                  style={styles.input}
                />
              </div>

              {loadingActivities && (
                <PanelLoader
                  compact
                  title="Refreshing activities"
                  description="Loading recent transaction and match activity for the active scope."
                />
              )}
              {!loadingActivities && !activities.length && (
                <div style={styles.detailEmptyState}>
                  <div style={styles.detailEmptyTitle}>No activities found</div>
                  <div style={styles.detailEmptyText}>
                    {isSuperadmin
                      ? 'Try switching tabs or adjusting the search to see more activity.'
                      : 'Transactions and match events for your customers will appear here.'}
                  </div>
                </div>
              )}
              {!loadingActivities && activities.length > 0 && (
                <div style={styles.tableWrap}>
                  <table style={{ ...styles.table, ...styles.activityTable, minWidth: isMobile ? 820 : 960 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Account</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Type</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Details</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead, textAlign: 'right' }}>Balance</th>
                        <th style={{ ...styles.tableHead, ...styles.activityTableHead }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((entry) => (
                        <tr key={entry._id}>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.activityTablePrimary }}>
                              {entry.user?.username || 'No username'}
                            </div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.activityTablePrimary }}>{humanizeTxType(entry.type)}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.activityTableSecondary }}>
                              {getTransactionActor(entry) || 'No extra details'}
                            </div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell, textAlign: 'right' }}>
                            <span style={{ ...styles.tableValueText, color: entry.amount >= 0 ? '#9b623f' : '#b86553' }}>
                              {entry.amount >= 0 ? '+' : ''}{formatNumber(entry.amount)}
                            </span>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell, textAlign: 'right' }}>
                            <span style={styles.tableValueText}>{formatNumber(entry.balanceAfter)}</span>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.activityTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.activityTableSecondary }}>{new Date(entry.createdAt).toLocaleString()}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={styles.paginationRow}>
                <button
                  type="button"
                  onClick={() => setActivityPage((current) => Math.max(1, current - 1))}
                  disabled={loadingActivities || activitiesMeta.page <= 1}
                  style={{ ...styles.quickBtn, ...(loadingActivities || activitiesMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                >
                  Previous
                </button>
                <div style={styles.paginationLabel}>
                  Page {formatNumber(activitiesMeta.page)} of {formatNumber(activitiesMeta.totalPages)}
                </div>
                <button
                  type="button"
                  onClick={() => setActivityPage((current) => Math.min(activitiesMeta.totalPages, current + 1))}
                  disabled={loadingActivities || activitiesMeta.page >= activitiesMeta.totalPages}
                  style={{ ...styles.quickBtn, ...(loadingActivities || activitiesMeta.page >= activitiesMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {view === VIEWS.games && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Games</div>
                  {/* <div style={styles.panelMeta}>
                    {isSuperadmin
                      ? 'Review recorded session summaries across the platform.'
                      : 'Review recorded session summaries played by your own customers.'}
                  </div> */}
                </div>
                <div style={styles.detailPill}>{formatNumber(gamesMeta.total)} Games</div>
              </div>

              <div style={styles.searchRow}>
                <select
                  value={gamesRange}
                  onChange={(e) => setGamesRange(e.target.value)}
                  style={{ ...styles.input, maxWidth: isMobile ? '100%' : 220 }}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Last 1 Week</option>
                  <option value="month">Last 1 Month</option>
                </select>
                <input
                  type="text"
                  value={gamesSearch}
                  onChange={(e) => setGamesSearch(e.target.value)}
                  placeholder="Search players by name or email"
                  style={styles.input}
                />
              </div>

              {loadingGames && (
                <PanelLoader
                  compact
                  title="Refreshing games"
                  description="Loading the latest completed sessions and player results."
                />
              )}
              {!loadingGames && !games.length && (
                <div style={styles.detailEmptyState}>
                  <div style={styles.detailEmptyTitle}>No games found</div>
                  <div style={styles.detailEmptyText}>
                    {isSuperadmin
                      ? 'Sessions will appear here as soon as players complete games.'
                      : 'Only sessions involving your own customers will appear here.'}
                  </div>
                </div>
              )}

              {!loadingGames && games.length > 0 && (
                <div style={styles.tableWrap}>
                  <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 920 : 1160 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Type</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Player 1</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Player 2</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Agents</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Winner</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Final Score</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Stake</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map((game) => (
                        <tr key={game.id}>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{game.typeLabel}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{game.playerOne?.username || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{game.playerTwo?.username || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>
                                {game.playerOne?.agentUsername || '-'} / {game.playerTwo?.agentUsername || '-'}
                              </div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{game.winnerName || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{String(game.finalScore || '').replace(/\s+/g, ' ').trim().match(/\d+\s*-\s*\d+/)?.[0] || game.finalScore}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}>
                              <span style={styles.tableValueText}>{formatNumber(game.stake)}</span>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>
                                {new Date(game.completedAt || game.createdAt).toLocaleString()}
                              </div>
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {games.length > 0 && (
                <div style={styles.paginationRow}>
                  <button
                    type="button"
                    onClick={() => setGamesPage((current) => Math.max(1, current - 1))}
                    disabled={loadingGames || gamesMeta.page <= 1}
                    style={{ ...styles.quickBtn, ...(loadingGames || gamesMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                  >
                    Previous
                  </button>
                  <div style={styles.paginationLabel}>
                    Page {formatNumber(gamesMeta.page)} of {formatNumber(gamesMeta.totalPages)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setGamesPage((current) => Math.min(gamesMeta.totalPages, current + 1))}
                    disabled={loadingGames || gamesMeta.page >= gamesMeta.totalPages}
                    style={{ ...styles.quickBtn, ...(loadingGames || gamesMeta.page >= gamesMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}

          {view === VIEWS.onlinePlayers && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Online Matches</div>
                </div>
                <div style={styles.detailPill}>{formatNumber(onlinePlayersMeta.total)} Matches</div>
              </div>

              <div style={styles.searchRow}>
                <input
                  type="text"
                  value={onlinePlayersSearch}
                  onChange={(e) => setOnlinePlayersSearch(e.target.value)}
                  placeholder="Search by username, agent, room, or result"
                  style={styles.input}
                />
              </div>

              {loadingOnlinePlayers && (
                <PanelLoader
                  compact
                  title="Refreshing online matches"
                  description="Loading the current live tables, usernames, agents, and running scores."
                />
              )}

              {!loadingOnlinePlayers && !onlinePlayers.length && (
                <div style={styles.detailEmptyState}>
                  <div style={styles.detailEmptyTitle}>No online matches found</div>
                  <div style={styles.detailEmptyText}>
                    {onlinePlayersSearch.trim()
                      ? 'Try a different search to find currently running matches.'
                      : 'Matches will appear here as soon as players start playing online.'}
                  </div>
                </div>
              )}

              {!loadingOnlinePlayers && onlinePlayers.length > 0 && (
                <div style={styles.tableWrap}>
                  <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 1240 : 1380 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Player 1</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Agent 1</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Player 2</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Agent 2</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Type</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Current Result</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Status</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Cyprus Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {onlinePlayers.map((match) => {
                        const statusLabel = match.paused ? 'Paused' : `Round ${formatNumber(match.roundNumber || 1)}`;
                        return (
                          <tr key={match.id}>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              {match.playerOne?.nativeUserId ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenCustomerDetails({ id: match.playerOne.nativeUserId })}
                                  style={styles.inlineLinkBtn}
                                >
                                  @{match.playerOne.username || '-'}
                                </button>
                              ) : (
                                <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>@{match.playerOne?.username || '-'}</div>
                              )}
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              {isSuperadmin && match.playerOne?.parentAgentId ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenAgentDetails({ id: match.playerOne.parentAgentId })}
                                  style={styles.inlineLinkBtn}
                                >
                                  {match.playerOne.parentAgentUsername || '-'}
                                </button>
                              ) : (
                                <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{match.playerOne?.parentAgentUsername || '-'}</div>
                              )}
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              {match.playerTwo?.nativeUserId ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenCustomerDetails({ id: match.playerTwo.nativeUserId })}
                                  style={styles.inlineLinkBtn}
                                >
                                  @{match.playerTwo.username || '-'}
                                </button>
                              ) : (
                                <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>@{match.playerTwo?.username || '-'}</div>
                              )}
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              {isSuperadmin && match.playerTwo?.parentAgentId ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenAgentDetails({ id: match.playerTwo.parentAgentId })}
                                  style={styles.inlineLinkBtn}
                                >
                                  {match.playerTwo.parentAgentUsername || '-'}
                                </button>
                              ) : (
                                <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{match.playerTwo?.parentAgentUsername || '-'}</div>
                              )}
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell, textAlign: 'right' }}>
                              <span style={styles.tableValueText}>{formatNumber(match.stake || 0)}</span>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{match.typeLabel || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{match.currentResult || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{statusLabel}</div>
                              <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{match.roomId || '-'}</div>
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                              <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>
                                {formatCyprusDateTime(match.startedAt)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {onlinePlayers.length > 0 && (
                <div style={styles.paginationRow}>
                  <button
                    type="button"
                    onClick={() => setOnlinePlayersPage((current) => Math.max(1, current - 1))}
                    disabled={loadingOnlinePlayers || onlinePlayersMeta.page <= 1}
                    style={{ ...styles.quickBtn, ...(loadingOnlinePlayers || onlinePlayersMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                  >
                    Previous
                  </button>
                  <div style={styles.paginationLabel}>
                    Page {formatNumber(onlinePlayersMeta.page)} of {formatNumber(onlinePlayersMeta.totalPages)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOnlinePlayersPage((current) => Math.min(onlinePlayersMeta.totalPages, current + 1))}
                    disabled={loadingOnlinePlayers || onlinePlayersMeta.page >= onlinePlayersMeta.totalPages}
                    style={{ ...styles.quickBtn, ...(loadingOnlinePlayers || onlinePlayersMeta.page >= onlinePlayersMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}

          {isSuperadmin && view === VIEWS.emailActivities && (
            <section style={styles.panel}>
              <div style={{ ...styles.listHeader, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.panelTitle}>Email Activities</div>
                </div>
                <div style={styles.detailPill}>{formatNumber(emailActivitiesMeta.total)} Emails</div>
              </div>

              <div style={styles.searchRow}>
                <input
                  type="text"
                  value={emailActivitiesSearch}
                  onChange={(e) => {
                    setEmailActivitiesSearch(e.target.value);
                    setEmailActivitiesPage(1);
                  }}
                  placeholder="Search by email, subject, category, or error"
                  style={styles.input}
                />
              </div>

              {loadingEmailActivities && (
                <PanelLoader
                  compact
                  title="Refreshing emails"
                  description="Loading recent email delivery activity."
                />
              )}

              {!loadingEmailActivities && !emailActivities.length && (
                <div style={styles.detailEmptyState}>
                  <div style={styles.detailEmptyTitle}>No email activities found</div>
                  <div style={styles.detailEmptyText}>
                    Email sends and failures will appear here.
                  </div>
                </div>
              )}

              {!loadingEmailActivities && emailActivities.length > 0 && (
                <div style={styles.tableWrap}>
                  <table style={{ ...styles.table, ...styles.listTable, minWidth: isMobile ? 980 : 1180 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>To</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Subject</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Category</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Template</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Status</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Error</th>
                        <th style={{ ...styles.tableHead, ...styles.listTableHead }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailActivities.map((entry) => (
                        <tr key={entry._id}>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{entry.to}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{entry.subject}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{entry.category || '-'}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{entry.templateKey || '-'}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tablePrimary, ...styles.listTablePrimary }}>{entry.status}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>{entry.errorMessage || '-'}</div>
                          </td>
                          <td style={{ ...styles.tableCell, ...styles.listTableCell }}>
                            <div style={{ ...styles.tableSecondary, ...styles.listTableSecondary }}>
                              {new Date(entry.createdAt).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {emailActivities.length > 0 && (
                <div style={styles.paginationRow}>
                  <button
                    type="button"
                    onClick={() => setEmailActivitiesPage((current) => Math.max(1, current - 1))}
                    disabled={loadingEmailActivities || emailActivitiesMeta.page <= 1}
                    style={{ ...styles.quickBtn, ...(loadingEmailActivities || emailActivitiesMeta.page <= 1 ? styles.quickBtnDisabled : {}) }}
                  >
                    Previous
                  </button>
                  <div style={styles.paginationLabel}>
                    Page {formatNumber(emailActivitiesMeta.page)} of {formatNumber(emailActivitiesMeta.totalPages)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEmailActivitiesPage((current) => Math.min(emailActivitiesMeta.totalPages, current + 1))}
                    disabled={loadingEmailActivities || emailActivitiesMeta.page >= emailActivitiesMeta.totalPages}
                    style={{ ...styles.quickBtn, ...(loadingEmailActivities || emailActivitiesMeta.page >= emailActivitiesMeta.totalPages ? styles.quickBtnDisabled : {}) }}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {showAgentModal && isSuperadmin && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.panelTitle}>Add Agent</div>
                <div style={styles.panelMeta}>Create a new agent and set commission while creating the account.</div>
              </div>
              <button type="button" onClick={() => setShowAgentModal(false)} style={styles.modalCloseBtn}>Close</button>
            </div>

            <form onSubmit={handleCreateAgent} style={styles.formGrid}>
              <label style={styles.label}>
                Agent Name
                <input value={agentForm.name} onChange={(e) => setAgentForm((prev) => ({ ...prev, name: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Username
                <input value={agentForm.username} onChange={(e) => setAgentForm((prev) => ({ ...prev, username: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Email
                <input type="email" value={agentForm.email} onChange={(e) => setAgentForm((prev) => ({ ...prev, email: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Password
                <input type="password" value={agentForm.password} onChange={(e) => setAgentForm((prev) => ({ ...prev, password: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Commission Rate (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={agentForm.commissionRate}
                  onChange={(e) => setAgentForm((prev) => ({ ...prev, commissionRate: e.target.value }))}
                  required
                  style={styles.input}
                />
              </label>

              <button type="submit" disabled={submittingCreate} style={styles.primaryBtn}>
                {submittingCreate ? 'Creating...' : 'Create Agent'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showCustomerModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.panelTitle}>Add Customer</div>
                <div style={styles.panelMeta}>
                  {isSuperadmin
                    ? 'Create a new customer without assigning an agent right now.'
                    : 'Create a new customer under your own agent account.'}
                </div>
              </div>
              <button type="button" onClick={() => setShowCustomerModal(false)} style={styles.modalCloseBtn}>Close</button>
            </div>

            <form onSubmit={handleCreateCustomer} style={styles.formGrid}>

              <label style={styles.label}>
                Customer Name
                <input value={createForm.name} onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Username
                <input value={createForm.username} onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Email
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))} required style={styles.input} />
              </label>

              <label style={styles.label}>
                Password
                <input type="password" value={createForm.password} onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))} required style={styles.input} />
              </label>

              <button type="submit" disabled={submittingCreate} style={styles.primaryBtn}>
                {submittingCreate ? 'Creating...' : 'Create Customer'}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={styles.infoCard}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, tone, compact = false }) {
  const tones = {
    cream: 'linear-gradient(145deg, rgba(255,250,246,0.96), rgba(248,240,233,0.98))',
    rose: 'linear-gradient(145deg, rgba(253,243,238,0.96), rgba(246,232,225,0.98))',
    amber: 'linear-gradient(145deg, rgba(252,241,227,0.96), rgba(243,228,208,0.98))',
  };

  return (
    <div style={{ ...styles.statCard, ...(compact ? styles.compactStatCard : {}), background: tones[tone] || tones.cream }}>
      <div style={{ ...styles.statLabel, ...(compact ? styles.compactStatLabel : {}) }}>{label}</div>
      <div style={{ ...styles.statValue, ...(compact ? styles.compactStatValue : {}) }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={styles.miniStatCard}>
      <div style={styles.miniStatValue}>{formatNumber(value)}</div>
      <div style={styles.miniStatLabel}>{label}</div>
    </div>
  );
}

function PanelLoader({ title, description, compact = false }) {
  return (
    <div style={{ ...styles.loaderCard, ...(compact ? styles.loaderCardCompact : {}) }}>
      <div style={styles.loaderVisual} aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <circle cx="15" cy="15" r="10" stroke="rgba(180,102,66,0.16)" strokeWidth="3" />
          <path d="M15 5a10 10 0 0 1 10 10" stroke="#b46642" strokeWidth="3" strokeLinecap="round">
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              from="0 15 15"
              to="360 15 15"
              dur="0.75s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>
      <div style={styles.loaderTitle}>{title}</div>
      <div style={styles.loaderText}>{description}</div>
      <div style={styles.loaderBarTrack}>
        <div style={styles.loaderBarFill} />
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1.5 12s3.75-6 10.5-6 10.5 6 10.5 6-3.75 6-10.5 6S1.5 12 1.5 12Z" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatCyprusDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-GB', {
    timeZone: 'Europe/Nicosia',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}


function humanizeTxType(type) {
  return String(type || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getTransactionActor(tx) {
  const metadata = tx?.metadata || {};
  const joiner = ' · ';
  const opponentLabel = metadata.opponentUsername || metadata.opponentName || '';
  const sessionLabel = metadata.targetWinsLabel || '';
  const playerOneLabel = metadata.playerOneUsername || metadata.playerOneName || '';
  const playerTwoLabel = metadata.playerTwoUsername || metadata.playerTwoName || '';
  const playerOneAgentLabel = metadata.playerOneAgentUsername || metadata.playerOneAgentName || '';
  const playerTwoAgentLabel = metadata.playerTwoAgentUsername || metadata.playerTwoAgentName || '';
  const baseMatchLabel = [opponentLabel ? `vs ${opponentLabel}` : '', sessionLabel].filter(Boolean).join(joiner);
  const superadminMatchLabel = [
    playerOneLabel && playerTwoLabel ? `${playerOneLabel} vs ${playerTwoLabel}` : '',
    sessionLabel,
  ].filter(Boolean).join(joiner);
  const superadminAgentLabel = [
    playerOneAgentLabel ? `A1 ${playerOneAgentLabel}` : 'A1 direct',
    playerTwoAgentLabel ? `A2 ${playerTwoAgentLabel}` : 'A2 direct',
  ].join(joiner);

  if (tx?.type === 'superadmin-credit') return metadata.superadminName || metadata.superadminEmail || '';
  if (tx?.type === 'superadmin-debit') return metadata.superadminName || metadata.superadminEmail || '';
  if (tx?.type === 'superadmin-transfer-out') {
    return metadata.targetUsername || metadata.targetName
      ? `Sent to ${metadata.targetUsername || metadata.targetName}`
      : '';
  }
  if (tx?.type === 'superadmin-transfer-in') {
    return metadata.targetUsername || metadata.targetName
      ? `Received from ${metadata.targetUsername || metadata.targetName}`
      : '';
  }
  if (tx?.type === 'agent-credit') {
    return metadata.customerName
      ? `Received from ${metadata.customerName}`
      : (metadata.agentName || metadata.agentEmail || '');
  }
  if (tx?.type === 'agent-debit') {
    return metadata.customerName
      ? `Transferred to ${metadata.customerName}`
      : (metadata.agentName || metadata.agentEmail || '');
  }
  if (tx?.type === 'agent-commission') {
    return metadata.playerUsername || metadata.playerName
      ? `Earned from ${metadata.playerUsername || metadata.playerName}`
      : '';
  }
  if (tx?.type === 'stake-lock') {
    return [baseMatchLabel, metadata.stake ? `Stake ${metadata.stake}` : ''].filter(Boolean).join(joiner);
  }
  if (tx?.type === 'platform-fee') {
    const label = metadata.source === 'online-match' ? superadminMatchLabel : baseMatchLabel;
    const breakdown = Array.isArray(metadata.contributorBreakdown)
      ? metadata.contributorBreakdown
          .map((entry) => {
            const sourceLabel = entry.agentUsername || entry.playerUsername || entry.playerName || 'direct';
            return `${sourceLabel} ${formatNumber(entry.amount)}`;
          })
          .join(' | ')
      : '';
    return [label, superadminAgentLabel, breakdown, metadata.fee ? `Platform fee ${formatNumber(metadata.fee)}` : '']
      .filter(Boolean)
      .join(joiner);
  }
  if (tx?.type === 'match-win') {
    return [baseMatchLabel, metadata.payout ? `Won ${formatNumber(metadata.payout)}` : ''].filter(Boolean).join(joiner);
  }
  if (tx?.type === 'refund') {
    return [baseMatchLabel, metadata.reason ? String(metadata.reason).replace(/-/g, ' ') : 'Refund'].filter(Boolean).join(joiner);
  }
  if (tx?.type === 'admin-credit') return metadata.adminName || metadata.adminEmail || '';
  return '';
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8f1ea 0%, #f2e5d9 100%)',
    padding: 20,
  },
  shell: {
    maxWidth: 1480,
    margin: '0 auto',
    display: 'grid',
    gap: 18,
    alignItems: 'start',
  },
  sidebar: {
    background: 'rgba(255, 251, 247, 0.92)',
    border: '1px solid rgba(166, 106, 75, 0.14)',
    borderRadius: 24,
    padding: 18,
    display: 'grid',
    gap: 14,
    alignContent: 'start',
    boxShadow: '0 24px 60px rgba(80,45,31,0.08)',
  },
  kicker: {
    color: '#b8623f',
    textTransform: 'uppercase',
    letterSpacing: 1.8,
    fontSize: 12,
    fontWeight: 800,
  },
  staffName: {
    color: '#241917',
    fontSize: 22,
    fontWeight: 800,
    marginTop: 4,
  },
  staffMeta: {
    color: '#7b665c',
    fontSize: 13,
    marginTop: 4,
  },
  balanceCard: {
    borderRadius: 18,
    padding: 14,
    background: 'linear-gradient(145deg, rgba(195,112,74,0.96), rgba(159,89,58,0.96))',
    color: '#fffaf7',
  },
  balanceLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    opacity: 0.8,
  },
  balanceValue: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.05,
    marginTop: 6,
  },
  balanceCaption: {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.86,
  },
  navList: {
    display: 'grid',
    gap: 8,
  },
  navBtn: {
    border: '1px solid rgba(166, 106, 75, 0.12)',
    background: '#f7efe8',
    color: '#4d3d37',
    borderRadius: 16,
    padding: '11px 12px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  navBtnActive: {
    background: 'linear-gradient(145deg, #d68860, #b46642)',
    color: '#fffaf7',
  },
  logoutBtn: {
    border: '1px solid rgba(166, 106, 75, 0.14)',
    background: '#fff8f3',
    color: '#372620',
    borderRadius: 14,
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 13,
  },
  mainArea: {
    display: 'grid',
    gap: 18,
    alignContent: 'start',
    alignItems: 'start',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  title: {
    margin: 0,
    color: '#201614',
    fontSize: 36,
    fontWeight: 800,
  },
  subTitle: {
    color: '#69564e',
    marginTop: 6,
    fontSize: 15,
    lineHeight: 1.5,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
  },
  compactStatsGrid: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
  },
  dashboardStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    alignItems: 'start',
  },
  realtimeTabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },
  statCard: {
    borderRadius: 20,
    padding: 14,
    border: '1px solid rgba(138,102,85,0.08)',
    boxShadow: '0 18px 45px rgba(80,45,31,0.08)',
    minHeight: 112,
    alignSelf: 'start',
  },
  compactStatCard: {
    borderRadius: 16,
    padding: 10,
    minHeight: 86,
  },
  statLabel: {
    color: '#8c7468',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  compactStatLabel: {
    fontSize: 10,
    letterSpacing: 0.8,
  },
  statValue: {
    color: '#231816',
    fontSize: 24,
    fontWeight: 800,
    marginTop: 8,
  },
  compactStatValue: {
    fontSize: 18,
    marginTop: 6,
    lineHeight: 1.25,
  },
  miniStatCard: {
    padding: 16,
    borderRadius: 18,
    background: 'rgba(248, 242, 237, 0.92)',
    border: '1px solid rgba(166, 106, 75, 0.08)',
  },
  miniStatValue: {
    color: '#241816',
    fontSize: 26,
    fontWeight: 800,
  },
  miniStatLabel: {
    color: '#7b665c',
    fontSize: 13,
    marginTop: 6,
  },
  feedback: {
    borderRadius: 16,
    padding: '13px 15px',
    fontWeight: 700,
  },
  feedbackSuccess: {
    background: 'rgba(255, 251, 247, 0.92)',
    border: '1px solid rgba(166, 106, 75, 0.12)',
    color: '#8c5a3c',
  },
  feedbackError: {
    background: 'rgba(255, 243, 239, 0.95)',
    border: '1px solid rgba(201, 91, 70, 0.16)',
    color: '#c95b46',
  },
  panel: {
    background: 'rgba(255, 251, 247, 0.9)',
    border: '1px solid rgba(166, 106, 75, 0.12)',
    borderRadius: 28,
    padding: 22,
    display: 'grid',
    gap: 16,
    boxShadow: '0 24px 60px rgba(80,45,31,0.08)',
    alignContent: 'start',
    alignSelf: 'start',
  },
  panelTitle: {
    color: '#211715',
    fontSize: 20,
    fontWeight: 800,
  },
  panelMeta: {
    color: '#7b665c',
    fontSize: 14,
    lineHeight: 1.5,
    marginTop: 4,
  },
  loaderCard: {
    minHeight: 112,
    display: 'grid',
    justifyItems: 'center',
    alignContent: 'center',
    gap: 8,
    padding: '16px 18px',
    borderRadius: 18,
    background: 'linear-gradient(145deg, rgba(255,250,246,0.98), rgba(246,237,229,0.98))',
    border: '1px solid rgba(166, 106, 75, 0.12)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 10px 24px rgba(80,45,31,0.05)',
    textAlign: 'center',
  },
  loaderCardCompact: {
    minHeight: 92,
    borderRadius: 16,
    padding: '14px 16px',
  },
  loaderVisual: {
    width: 42,
    height: 42,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 999,
    background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(242,227,215,0.9))',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
  },
  loaderTitle: {
    color: '#241816',
    fontSize: 15,
    fontWeight: 800,
  },
  loaderText: {
    color: '#7b665c',
    fontSize: 12,
    lineHeight: 1.45,
    maxWidth: 360,
  },
  loaderBarTrack: {
    width: '100%',
    maxWidth: 180,
    height: 5,
    borderRadius: 999,
    overflow: 'hidden',
    background: 'rgba(180, 102, 66, 0.12)',
  },
  loaderBarFill: {
    width: '62%',
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(214,136,96,0.9), rgba(180,102,66,1))',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
    alignItems: 'end',
  },
  label: {
    display: 'grid',
    gap: 8,
    color: '#54433c',
    fontWeight: 600,
    fontSize: 14,
  },
  input: {
    border: '1px solid rgba(166, 106, 75, 0.14)',
    background: 'rgba(247, 241, 235, 0.94)',
    color: '#251916',
    borderRadius: 14,
    padding: '13px 14px',
    outline: 'none',
    width: '100%',
  },
  primaryBtn: {
    border: '1px solid rgba(184, 105, 67, 0.26)',
    background: 'linear-gradient(145deg, #d68860, #b46642)',
    color: '#fffaf7',
    borderRadius: 14,
    padding: '13px 18px',
    cursor: 'pointer',
    fontWeight: 800,
    boxShadow: '0 16px 35px rgba(120,70,47,0.16)',
  },
  tableWrap: {
    overflowX: 'auto',
    border: '1px solid rgba(166, 106, 75, 0.12)',
    borderRadius: 14,
    background: '#fffaf7',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 760,
  },
  listTable: {
    minWidth: 0,
  },
  activityTable: {
    minWidth: 0,
  },
  tableHead: {
    textAlign: 'left',
    padding: '14px 16px',
    color: '#7b665c',
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
    borderBottom: '1px solid rgba(166, 106, 75, 0.12)',
  },
  listTableHead: {
    padding: '8px 12px',
    fontSize: 12,
    background: '#f6efe9',
  },
  activityTableHead: {
    padding: '9px 12px',
    fontSize: 12,
    background: '#f6efe9',
  },
  tableCell: {
    padding: '16px',
    color: '#2a1c18',
    borderBottom: '1px solid rgba(166, 106, 75, 0.08)',
    verticalAlign: 'top',
  },
  listTableCell: {
    padding: '5px 12px',
    lineHeight: 1.1,
  },
  activityTableCell: {
    padding: '8px 12px',
  },
  tablePrimary: {
    fontWeight: 800,
    color: '#241816',
    fontSize: 14,
    lineHeight: 1.3,
  },
  listTablePrimary: {
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 400,
  },
  activityTablePrimary: {
    fontSize: 14,
    lineHeight: 1.3,
    fontWeight: 400,
  },
  tableSecondary: {
    marginTop: 4,
    color: '#8b776d',
    fontSize: 13,
  },
  tableValueText: {
    color: '#2a1c18',
    fontSize: 14,
    lineHeight: 1.15,
    fontWeight: 400,
  },
  listTableSecondary: {
    marginTop: 1,
    fontSize: 13,
    lineHeight: 1.3,
  },
  activityTableSecondary: {
    marginTop: 1,
    fontSize: 13,
    lineHeight: 1.3,
  },
  iconBtn: {
    width: 40,
    height: 40,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 12,
    border: '1px solid rgba(166, 106, 75, 0.16)',
    background: '#fff8f3',
    color: '#6e4a37',
    cursor: 'pointer',
  },
  listIconBtn: {
    width: 28,
    height: 28,
    display: 'inline-grid',
    placeItems: 'center',
    borderRadius: 7,
    border: '1px solid rgba(166, 106, 75, 0.12)',
    background: 'transparent',
    color: '#6e4a37',
    cursor: 'pointer',
  },
  inlineLinkBtn: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#6e4a37',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 400,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    textAlign: 'left',
  },
  agentDetailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 14,
    alignItems: 'start',
  },
  detailSectionCard: {
    display: 'grid',
    gap: 12,
    padding: 16,
    borderRadius: 24,
    background: 'linear-gradient(145deg, rgba(255,250,246,0.96), rgba(247,240,233,0.98))',
    border: '1px solid rgba(166, 106, 75, 0.12)',
    boxShadow: '0 18px 45px rgba(80,45,31,0.06)',
    minHeight: 0,
    alignContent: 'start',
  },
  detailSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  detailPill: {
    borderRadius: 999,
    padding: '8px 12px',
    background: 'rgba(184, 105, 67, 0.08)',
    color: '#7d563f',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  detailEmptyState: {
    minHeight: 130,
    display: 'grid',
    alignContent: 'center',
    justifyItems: 'start',
    padding: 16,
    borderRadius: 20,
    background: 'rgba(248, 242, 237, 0.92)',
    border: '1px dashed rgba(166, 106, 75, 0.2)',
  },
  detailEmptyTitle: {
    color: '#251916',
    fontSize: 20,
    fontWeight: 800,
  },
  detailEmptyText: {
    marginTop: 8,
    color: '#7b665c',
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 420,
  },
  toggleRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  directoryGrid: {
    display: 'grid',
    gap: 18,
    alignItems: 'start',
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  searchRow: {
    display: 'grid',
  },
  userList: {
    display: 'grid',
    gap: 10,
    maxHeight: 'min(62vh, 640px)',
    overflowY: 'auto',
    paddingRight: 4,
  },
  userRow: {
    border: '1px solid rgba(166, 106, 75, 0.1)',
    background: 'rgba(250, 245, 240, 0.92)',
    borderRadius: 18,
    padding: '14px 15px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    gap: 12,
  },
  userRowActive: {
    background: 'linear-gradient(145deg, rgba(255, 242, 233, 0.98), rgba(249, 233, 223, 0.98))',
    borderColor: 'rgba(184, 105, 67, 0.38)',
  },
  userName: {
    color: '#241816',
    fontSize: 16,
    fontWeight: 800,
  },
  userMeta: {
    color: '#8b776d',
    fontSize: 12,
    marginTop: 5,
  },
  userHint: {
    color: '#6e5a51',
    fontSize: 12,
    marginTop: 5,
  },
  userRight: {
    display: 'grid',
    justifyItems: 'end',
    gap: 8,
  },
  roleChip: {
    borderRadius: 999,
    padding: '6px 10px',
    background: 'rgba(184, 105, 67, 0.08)',
    color: '#7d563f',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'capitalize',
  },
  userCoins: {
    color: '#9b623f',
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  detailHero: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  detailRole: {
    color: '#b8623f',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: 800,
  },
  detailName: {
    color: '#211715',
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 1.05,
    marginTop: 6,
  },
  detailMeta: {
    color: '#78645b',
    marginTop: 6,
    fontSize: 13,
  },
  detailBalance: {
    borderRadius: 20,
    padding: 16,
    background: 'linear-gradient(145deg, rgba(195,112,74,0.96), rgba(159,89,58,0.96))',
    color: '#fffaf7',
    minWidth: 220,
  },
  detailBalanceLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    opacity: 0.78,
  },
  detailBalanceValue: {
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1.05,
    marginTop: 8,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
  },
  infoCard: {
    padding: 12,
    borderRadius: 16,
    background: 'rgba(248, 242, 237, 0.92)',
    border: '1px solid rgba(166, 106, 75, 0.08)',
  },
  infoLabel: {
    color: '#8d786f',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 6,
  },
  infoValue: {
    color: '#251916',
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  rechargeCard: {
    display: 'grid',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    background: 'linear-gradient(145deg, rgba(252,244,238,0.98), rgba(246,236,229,0.96))',
    border: '1px solid rgba(166, 106, 75, 0.12)',
  },
  quickAmounts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
  },
  quickBtn: {
    borderRadius: 14,
    border: '1px solid rgba(166, 106, 75, 0.14)',
    background: 'rgba(247, 241, 235, 0.94)',
    color: '#513f39',
    padding: '11px 10px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  quickBtnActive: {
    background: 'linear-gradient(145deg, rgba(214,132,93,0.96), rgba(182,103,67,0.96))',
    borderColor: 'rgba(184, 105, 67, 0.34)',
    color: '#fffaf7',
  },
  quickBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  transferHint: {
    borderRadius: 14,
    padding: '12px 14px',
    background: 'rgba(247, 241, 235, 0.94)',
    border: '1px solid rgba(166, 106, 75, 0.08)',
    color: '#7b665c',
    fontSize: 13,
    fontWeight: 700,
  },
  transactionsWrap: {
    display: 'grid',
    gap: 12,
  },
  transactionsList: {
    display: 'grid',
    gap: 8,
    maxHeight: 'min(38vh, 340px)',
    overflowY: 'auto',
    paddingRight: 4,
  },
  playerTableWrap: {
    maxHeight: 'min(42vh, 420px)',
    overflowY: 'auto',
  },
  transactionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    borderRadius: 14,
    padding: '10px 12px',
    background: 'rgba(248, 242, 237, 0.94)',
    border: '1px solid rgba(166, 106, 75, 0.1)',
  },
  transactionType: {
    color: '#251916',
    fontWeight: 700,
    fontSize: 13,
  },
  transactionMeta: {
    color: '#8b776d',
    fontSize: 11,
    marginTop: 2,
    lineHeight: 1.35,
  },
  transactionAmount: {
    fontWeight: 800,
    whiteSpace: 'nowrap',
    fontSize: 16,
  },
  activityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-start',
    borderRadius: 18,
    padding: '16px 17px',
    background: 'rgba(248, 242, 237, 0.94)',
    border: '1px solid rgba(166, 106, 75, 0.1)',
  },
  activityBody: {
    minWidth: 0,
    flex: 1,
  },
  activityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
  },
  activitySubject: {
    color: '#5d4a43',
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
    wordBreak: 'break-word',
  },
  activityAmountWrap: {
    minWidth: 110,
    textAlign: 'right',
  },
  paginationRow: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  paginationLabel: {
    color: '#6f5b52',
    fontWeight: 700,
  },
  empty: {
    color: '#7a655c',
    padding: 14,
    minHeight: 80,
    display: 'grid',
    alignItems: 'center',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(43, 26, 19, 0.42)',
    display: 'grid',
    placeItems: 'center',
    padding: 20,
    zIndex: 1000,
  },
  modalCard: {
    width: 'min(960px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'rgba(255, 251, 247, 0.98)',
    border: '1px solid rgba(166, 106, 75, 0.14)',
    borderRadius: 28,
    padding: 22,
    boxShadow: '0 32px 80px rgba(80,45,31,0.16)',
    display: 'grid',
    gap: 16,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  modalCloseBtn: {
    border: '1px solid rgba(166, 106, 75, 0.14)',
    background: '#fff8f3',
    color: '#372620',
    borderRadius: 14,
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: 700,
  },
};






